import React, { useContext, useState, useEffect ,useCallback } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Row, Col, Button, Modal, Form, Alert, Spinner, Accordion, Badge, Table } from "react-bootstrap";
import axios from "axios";
import { FaEdit, FaTrash, FaPlus, FaStethoscope, FaFileAlt, FaPills, FaTrashAlt, FaShoppingCart, FaBoxes, FaCheckSquare, FaSquare, FaMoneyBillWave, FaSave } from "react-icons/fa";
import moment from "moment";
import { MdDateRange, MdAccessTime } from "react-icons/md";
import Select from "react-select";
import Swal from 'sweetalert2';

export default function PetInhousePrescriptionComponent({ appointmentId, petId, isAppointmentOpen }) {
    const { t } = useContext(TranslatorContext);
    const [petPrescriptions, setPetPrescriptions] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingPrescription, setEditingPrescription] = useState(null);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [generalNote, setGeneralNote] = useState("");
    const [existingNoteId, setExistingNoteId] = useState(null);
    const [openAccordion, setOpenAccordion] = useState(null);
    const [openNestedAccordion, setOpenNestedAccordion] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [productSearchTerms, setProductSearchTerms] = useState({});
    const [editingTableRows, setEditingTableRows] = useState({});
    const [hasPrescriptionForAppointment, setHasPrescriptionForAppointment] = useState(false);
    

    // Get user role from localStorage
    const userRole = localStorage.getItem("role");
    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];

    const hasPermission = (perm) => {
        return permissions.includes(perm);
    };

    // Delete permission only for Admin
    const canDelete = () => {
        return userRole === 'Admin';
    };

    // Edit permission for prescription
    const canEditPrescription = () => {
        return hasPermission("Appointment Prescription edit");
    };

    // Helper function to format price without .00 if whole number
    const formatPrice = (price) => {
        if (price === null || price === undefined) return '0';
        const num = parseFloat(price);
        if (isNaN(num)) return '0';
        return num % 1 === 0 ? num : num;
    };

    // Toast notification functions
    const showSuccessToast = (message) => {
        Swal.fire({
            icon: 'success',
            title: t('success'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
        });
    };

    const showErrorToast = (message) => {
        Swal.fire({
            icon: 'error',
            title: t('error'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
        });
    };

    const showWarningToast = (message) => {
        Swal.fire({
            icon: 'warning',
            title: t('warning'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
        });
    };

    useEffect(() => {
        fetchPetPrescriptions();
    }, [petId, appointmentId]);

    const fetchPetPrescriptions = async () => {
        try {
            setLoading(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/pet-inhouse-prescription/${petId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data) {
                const prescriptions = response.data.petPrescription || [];
                setPetPrescriptions(prescriptions);
                setProducts(response.data.products || []);
                const hasPrescription = prescriptions.some(prescription =>
                    prescription.appointment_id === appointmentId || prescription.appointment?.id === appointmentId
                );
                setHasPrescriptionForAppointment(hasPrescription);
                // Set current appointment accordion to open by default
                setOpenAccordion(String(appointmentId));
            }
        } catch (error) {
            console.error("Error fetching pet prescriptions:", error);
            showErrorToast(t('failed_to_load_prescriptions'));
            setPetPrescriptions([]);
            setProducts([]);
        } finally {
            setLoading(false);
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return null;
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return null;
            return {
                date: date.toLocaleDateString(),
                time: date.toLocaleTimeString(),
                full: date.toLocaleString()
            };
        } catch (error) {
            return null;
        }
    };

    const formatTimeDisplay = (timeString) => {
        if (!timeString) return 'N/A';
        try {
            const [hours, minutes] = timeString.split(':');
            let hr = parseInt(hours, 10);
            const ampm = hr >= 12 ? 'PM' : 'AM';
            hr = hr % 12 || 12;
            return `${hr}:${minutes} ${ampm}`;
        } catch (error) {
            return 'N/A';
        }
    };

    const handleAddPrescription = () => {
        if (!canEditPrescription()) {
            showWarningToast(t('you_dont_have_permission_to_add'));
            return;
        }
        setEditingPrescription(null);
        setSelectedProducts([]);
        setGeneralNote("");
        setExistingNoteId(null);
        setProductSearchTerms({});
        setShowModal(true);
    };

    const handleEditPrescription = (prescription) => {
        if (!canEditPrescription()) {
            showWarningToast(t('you_dont_have_permission_to_edit'));
            return;
        }
        setEditingPrescription(prescription);

        const existingProducts = prescription.prescription_detail?.map(detail => {
            const productFromList = products.find(p => p.id === detail.product_id);
            const currentStock = productFromList?.stock || detail.product?.stock || 0;

            return {
                id: detail.id || detail.product_id,
                product_id: detail.product_id,
                product_name: detail.product?.product?.name || detail.product?.name || 'N/A',
                quantity: detail.quantity,
                price: detail.price,
                purchase_detail_id: detail.purchase_detail_id,
                stock: currentStock,
                in_prescription: detail.in_prescription !== undefined ? detail.in_prescription : 1
            };
        }) || [];

        setSelectedProducts(existingProducts);

        if (prescription.note && Array.isArray(prescription.note) && prescription.note.length > 0) {
            setGeneralNote(prescription.note[0].note || "");
            setExistingNoteId(prescription.note[0].id);
        } else {
            setGeneralNote("");
            setExistingNoteId(null);
        }

        setProductSearchTerms({});
        setShowModal(true);
    };

    const handleDeletePrescription = async (prescriptionId) => {
        if (!canDelete()) {
            showWarningToast(t('only_admin_can_delete'));
            return;
        }

        try {
            setSubmitting(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.delete(
                `${process.env.REACT_APP_API_URL}/pet-inhouse-prescription-delete/${prescriptionId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data) {
                fetchPetPrescriptions();
                showSuccessToast(t('Prescription deleted successfully!'));
            }
        } catch (error) {
            console.error("Error deleting prescription:", error);
            showErrorToast(t('failed_to_delete_prescription'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = (prescriptionId, prescriptionName) => {
        Swal.fire({
            title: t('Are you sure?'),
            text: t("You won't be able to revert this!"),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('Yes, delete it!'),
            cancelButtonText: t('Cancel'),
        }).then((result) => {
            if (result.isConfirmed) {
                handleDeletePrescription(prescriptionId);
            }
        });
    };

    const handleAddTableRow = (prescriptionId) => {
        const newRow = {
            tempId: `temp_${Date.now()}_${Math.random()}`,
            product_id: null,
            product_name: '',
            quantity: 1,
            price: 0,
            purchase_detail_id: null,
            stock: 0,
            in_prescription: 1,
            isNew: true
        };

        setPetPrescriptions(prevPrescriptions =>
            prevPrescriptions.map(prescription => {
                if (prescription.id === prescriptionId) {
                    return {
                        ...prescription,
                        prescription_detail: [...(prescription.prescription_detail || []), newRow]
                    };
                }
                return prescription;
            })
        );

        setEditingTableRows(prev => ({ ...prev, [prescriptionId]: true }));
    };

    const handleTableProductSelect = (prescriptionId, rowId, selectedOption) => {
        if (!selectedOption) {
            setPetPrescriptions(prevPrescriptions =>
                prevPrescriptions.map(prescription => {
                    if (prescription.id === prescriptionId) {
                        return {
                            ...prescription,
                            prescription_detail: prescription.prescription_detail.map(row =>
                                row.tempId === rowId || row.id === rowId ? {
                                    ...row,
                                    product_id: null,
                                    product_name: '',
                                    price: 0,
                                    stock: 0,
                                    purchase_detail_id: null,
                                    quantity: 1
                                } : row
                            )
                        };
                    }
                    return prescription;
                })
            );
            return;
        }

        const selectedProduct = products.find(p => p.id === selectedOption.value);
        if (selectedProduct) {
            const currentStock = selectedProduct.stock || 0;
            setPetPrescriptions(prevPrescriptions =>
                prevPrescriptions.map(prescription => {
                    if (prescription.id === prescriptionId) {
                        return {
                            ...prescription,
                            prescription_detail: prescription.prescription_detail.map(row =>
                                row.tempId === rowId || row.id === rowId ? {
                                    ...row,
                                    product_id: selectedProduct.id,
                                    product_name: selectedProduct.product_name || selectedProduct.name || 'N/A',
                                    price: selectedProduct.sale_price || selectedProduct.price || 0,
                                    stock: currentStock,
                                    purchase_detail_id: selectedProduct.id,
                                    quantity: Math.min(row.quantity || 1, currentStock),
                                    isNew: true
                                } : row
                            )
                        };
                    }
                    return prescription;
                })
            );
            setEditingTableRows(prev => ({ ...prev, [prescriptionId]: true }));
        }
    };

    const handleTableQuantityUpdate = (prescriptionId, rowId, newQuantity) => {
        const prescription = petPrescriptions.find(p => p.id === prescriptionId);
        const row = prescription?.prescription_detail?.find(r => (r.tempId === rowId || r.id === rowId));
        const maxStock = row?.stock || 0;

        if (newQuantity < 1) {
            handleTableRemoveRow(prescriptionId, rowId);
        } else if (newQuantity <= maxStock) {
            setPetPrescriptions(prevPrescriptions =>
                prevPrescriptions.map(prescription => {
                    if (prescription.id === prescriptionId) {
                        return {
                            ...prescription,
                            prescription_detail: prescription.prescription_detail.map(row =>
                                row.tempId === rowId || row.id === rowId ? {
                                    ...row,
                                    quantity: newQuantity
                                } : row
                            )
                        };
                    }
                    return prescription;
                })
            );
            setEditingTableRows(prev => ({ ...prev, [prescriptionId]: true }));
        } else {
            showWarningToast(`${t('Cannot exceed available stock')}. ${t('Max stock')}: ${maxStock}`);
        }
    };

    const handleTablePriceUpdate = (prescriptionId, rowId, newPrice) => {
        const price = parseFloat(newPrice) || 0;
        if (price < 0) return;

        setPetPrescriptions(prevPrescriptions =>
            prevPrescriptions.map(prescription => {
                if (prescription.id === prescriptionId) {
                    return {
                        ...prescription,
                        prescription_detail: prescription.prescription_detail.map(row =>
                            row.tempId === rowId || row.id === rowId ? {
                                ...row,
                                price: price
                            } : row
                        )
                    };
                }
                return prescription;
            })
        );
        setEditingTableRows(prev => ({ ...prev, [prescriptionId]: true }));
    };

    const handleTableInPrescriptionToggle = (prescriptionId, rowId) => {
        setPetPrescriptions(prevPrescriptions =>
            prevPrescriptions.map(prescription => {
                if (prescription.id === prescriptionId) {
                    return {
                        ...prescription,
                        prescription_detail: prescription.prescription_detail.map(row =>
                            row.tempId === rowId || row.id === rowId ? {
                                ...row,
                                in_prescription: row.in_prescription === 1 ? 0 : 1,
                                isNew: true
                            } : row
                        )
                    };
                }
                return prescription;
            })
        );
        setEditingTableRows(prev => ({ ...prev, [prescriptionId]: true }));
    };

    const handleTableRemoveRow = (prescriptionId, rowId) => {
        Swal.fire({
            title: t('Remove Item'),
            text: t('Are you sure you want to remove this item?'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('Yes, remove it!'),
            cancelButtonText: t('Cancel'),
        }).then((result) => {
            if (result.isConfirmed) {
                setPetPrescriptions(prevPrescriptions =>
                    prevPrescriptions.map(prescription => {
                        if (prescription.id === prescriptionId) {
                            return {
                                ...prescription,
                                prescription_detail: prescription.prescription_detail.filter(row =>
                                    (row.tempId !== rowId && row.id !== rowId)
                                )
                            };
                        }
                        return prescription;
                    })
                );
                setEditingTableRows(prev => ({ ...prev, [prescriptionId]: true }));
                showSuccessToast(t('Item removed successfully'));
            }
        });
    };

    const handleSaveTableChanges = async (prescription) => {
        const validProducts = prescription.prescription_detail?.filter(p => p.product_id && p.quantity > 0 && !p.tempId) || [];
        const newProducts = prescription.prescription_detail?.filter(p => p.product_id && p.quantity > 0 && p.tempId) || [];

        if (newProducts.length === 0 && validProducts.length === 0) {
            showWarningToast(t('please_select_at_least_one_product'));
            return;
        }

        const allProducts = [...validProducts, ...newProducts];
        const stockErrors = allProducts.filter(p => p.quantity > (p.stock || 0));
        if (stockErrors.length > 0) {
            showWarningToast(`${t('Some products exceed available stock')}. ${t('Please check quantities')}.`);
            return;
        }

        setSubmitting(true);

        try {
            const AuthToken = localStorage.getItem("authToken");
            const url = `${process.env.REACT_APP_API_URL}/pet-inhouse-prescription-update/${prescription.id}`;

            const payload = {
                appointment_id: appointmentId,
                product_ids: allProducts.map(p => ({
                    id: p.purchase_detail_id || p.product_id,
                    quantity: p.quantity,
                    price: p.price,
                    is_prescription: p.in_prescription !== undefined ? p.in_prescription : 1
                }))
            };

            const response = await axios({
                method: 'post',
                url: url,
                data: payload,
                headers: { Authorization: `Bearer ${AuthToken}` }
            });

            if (response.data) {
                await fetchPetPrescriptions();
                setEditingTableRows(prev => ({ ...prev, [prescription.id]: false }));
                showSuccessToast(t('Prescription updated successfully!'));
            }
        } catch (error) {
            console.error("Error saving prescription:", error);
            showErrorToast(error.response?.data?.message || t('failed_to_save_prescription'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleAddNewRow = () => {
        const newId = Date.now();
        setSelectedProducts([...selectedProducts, {
            id: newId,
            product_id: null,
            product_name: '',
            quantity: 1,
            price: 0,
            purchase_detail_id: null,
            stock: 0,
            in_prescription: 1,
            isNew: true
        }]);
    };

    const handleProductSelect = (productId, rowId, selectedOption) => {
        if (!selectedOption) {
            setSelectedProducts(selectedProducts.map(p =>
                p.id === rowId ? {
                    ...p,
                    product_id: null,
                    product_name: '',
                    price: 0,
                    stock: 0,
                    purchase_detail_id: null,
                    quantity: 1
                } : p
            ));
            return;
        }

        const selectedProduct = products.find(p => p.id === selectedOption.value);
        if (selectedProduct) {
            const currentStock = selectedProduct.stock || 0;
            setSelectedProducts(selectedProducts.map(p =>
                p.id === rowId ? {
                    ...p,
                    product_id: selectedProduct.id,
                    product_name: selectedProduct.product_name || selectedProduct.name || 'N/A',
                    price: selectedProduct.sale_price || selectedProduct.price || 0,
                    stock: currentStock,
                    purchase_detail_id: selectedProduct.id,
                    quantity: Math.min(p.quantity, currentStock)
                } : p
            ));
        }
        setProductSearchTerms(prev => ({ ...prev, [rowId]: '' }));
    };

    const handleUpdatePrice = (productId, newPrice) => {
        const price = parseFloat(newPrice) || 0;
        if (price < 0) return;

        setSelectedProducts(selectedProducts.map(p =>
            p.id === productId ? { ...p, price: price } : p
        ));
    };

    const handleUpdateQuantity = (productId, newQuantity) => {
        const product = selectedProducts.find(p => p.id === productId);
        const maxStock = product?.stock || 0;

        if (newQuantity < 1) {
            setSelectedProducts(selectedProducts.filter(p => p.id !== productId));
        } else if (newQuantity <= maxStock) {
            setSelectedProducts(selectedProducts.map(p =>
                p.id === productId ? { ...p, quantity: newQuantity } : p
            ));
        } else {
            showWarningToast(`${t('Cannot exceed available stock')}. ${t('Max stock')}: ${maxStock}`);
        }
    };

    const handleRemoveProduct = (productId) => {
        setSelectedProducts(selectedProducts.filter(p => p.id !== productId));
    };

    const handleToggleInPrescription = (productId) => {
        setSelectedProducts(selectedProducts.map(p =>
            p.id === productId ? { ...p, in_prescription: p.in_prescription === 1 ? 0 : 1 } : p
        ));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const validProducts = selectedProducts.filter(p => p.product_id && p.quantity > 0);

        if (validProducts.length === 0) {
            showWarningToast(t('please_select_at_least_one_product'));
            return;
        }

        const stockErrors = validProducts.filter(p => p.quantity > (p.stock || 0));
        if (stockErrors.length > 0) {
            showWarningToast(`${t('Some products exceed available stock')}. ${t('Please check quantities')}.`);
            return;
        }

        setSubmitting(true);

        try {
            const AuthToken = localStorage.getItem("authToken");
            const url = editingPrescription
                ? `${process.env.REACT_APP_API_URL}/pet-inhouse-prescription-update/${editingPrescription.id}`
                : `${process.env.REACT_APP_API_URL}/pet-inhouse-prescription`;

            const payload = {
                appointment_id: appointmentId,
                product_ids: validProducts.map(p => ({
                    id: p.purchase_detail_id || p.product_id,
                    quantity: p.quantity,
                    price: p.price,
                    is_prescription: p.in_prescription !== undefined ? p.in_prescription : 1
                }))
            };

            if (generalNote && generalNote.trim()) {
                payload.note = generalNote.trim();
                if (existingNoteId) {
                    payload.note_id = existingNoteId;
                }
            }

            const response = await axios({
                method: 'post',
                url: url,
                data: payload,
                headers: { Authorization: `Bearer ${AuthToken}` }
            });

            if (response.data) {
                setShowModal(false);
                fetchPetPrescriptions();
                showSuccessToast(editingPrescription ? t('Prescription updated successfully!') : t('Prescription added successfully!'));
            }
        } catch (error) {
            console.error("Error saving prescription:", error);
            showErrorToast(error.response?.data?.message || t('failed_to_save_prescription'));
        } finally {
            setSubmitting(false);
        }
    };

    const getProductName = (product) => {
        return product.product_name || product.name || 'N/A';
    };

    const productOptions = products.map(product => ({
        value: product.id,
        label: `${getProductName(product)} `,
        stock: product.stock || 0,
        sale_price: product.sale_price || product.price || 0
    }));

    const groupPrescriptionsByAppointment = () => {
        const grouped = {};
        petPrescriptions.forEach(prescription => {
            const appointmentIdKey = prescription.appointment_id || prescription.appointment?.id;
            if (!grouped[appointmentIdKey]) {
                grouped[appointmentIdKey] = {
                    appointment: prescription.appointment,
                    prescriptions: []
                };
            }
            grouped[appointmentIdKey].prescriptions.push(prescription);
        });

        const sortedEntries = Object.entries(grouped).sort((a, b) => {
            const aId = String(a[0]);
            const bId = String(b[0]);
            const currentId = String(appointmentId);

            if (aId === currentId) return -1;
            if (bId === currentId) return 1;
            return 0;
        });

        return Object.fromEntries(sortedEntries);
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 mb-0">{t('loading prescriptions')}</p>
            </div>
        );
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                <h6 className="mb-0 text-muted">{t('inhouse prescriptions list')}</h6>
                {isAppointmentOpen && !hasPrescriptionForAppointment &&
                    hasPermission("Appointment Prescription Create") && canEditPrescription() && (
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={handleAddPrescription}
                            className="d-flex align-items-center gap-2"
                        >
                            <FaPlus size={12} /> {t('add prescription')}
                        </Button>
                    )}
            </div>

            {!petPrescriptions || petPrescriptions.length === 0 ? (
                <Alert variant="info" className="text-center py-4">
                    <FaPills size={30} className="mb-2 text-muted" />
                    <p className="mb-0">{t('no prescriptions found')}</p>
                </Alert>
            ) : (
                <Accordion
                    activeKey={openAccordion}
                    onSelect={(key) => setOpenAccordion(key)}
                    className="pet-prescriptions-accordion"
                >
                    {Object.entries(groupPrescriptionsByAppointment()).map(([appointmentIdKey, appointmentData]) => {
                        const { appointment, prescriptions: appointmentPrescriptions } = appointmentData;
                        const isCurrentAppointment = String(appointmentIdKey) === String(appointmentId);

                        return (
                            <Accordion.Item eventKey={appointmentIdKey} key={appointmentIdKey} className="mb-3 border rounded">
                                <Accordion.Header className="bg-light">
                                    <div className="d-flex justify-content-between align-items-center w-100 me-3 pe-3">
                                        <div className="d-flex flex-column flex-md-row align-items-md-center gap-2 gap-md-3">
                                            <div className="text-muted small">
                                                <strong className="text-primary">{appointment?.appointment_number || 'Unknown Appointment'}</strong>
                                            </div>
                                            <div className="text-muted small">
                                                👨‍⚕️ <strong>Doctor:</strong> {appointment?.doctor?.name || 'Unknown Doctor'}
                                            </div>
                                            <Badge bg="info" className="px-3 py-2">
                                                {appointmentPrescriptions.length} {t('prescription(s)')}
                                            </Badge>
                                        </div>
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body className="bg-white p-0">
                                    <Accordion
                                        activeKey={openNestedAccordion[appointmentIdKey]}
                                        onSelect={(key) => setOpenNestedAccordion({
                                            ...openNestedAccordion,
                                            [appointmentIdKey]: key
                                        })}
                                        className="nested-prescriptions-accordion pe-3 ps-3 pt-3"
                                    >
                                        {appointmentPrescriptions.map((prescription) => {
                                            if (!prescription) return null;
                                            const dateTime = formatDateTime(prescription.created_at);
                                            const noteText = prescription.note && Array.isArray(prescription.note) && prescription.note.length > 0
                                                ? prescription.note[0].note
                                                : null;
                                            const totalAmount = prescription.prescription_detail?.reduce((sum, detail) =>
                                                sum + (detail.price * (detail.quantity || 1)), 0) || 0;
                                            const hasUnsavedChanges = editingTableRows[prescription.id];

                                            return (
                                                <Accordion.Item eventKey={String(prescription.id)} key={prescription.id} className="mb-3 border rounded">
                                                    <Accordion.Header className="bg-light">
                                                        <div className="d-flex justify-content-between align-items-center w-100 me-3 pe-3">
                                                            <div className="d-flex flex-column flex-md-row align-items-md-center gap-2 gap-md-3">
                                                                <FaStethoscope className="text-primary" />
                                                                <Badge bg="info" className="px-3 py-2">
                                                                    {prescription.prescription_detail?.length || 0} {t('items')}
                                                                </Badge>
                                                                {dateTime && (
                                                                    <div className="d-flex align-items-center gap-3 text-muted small">
                                                                        <span className="d-flex align-items-center gap-1">
                                                                            <MdDateRange size={14} />
                                                                            {moment(dateTime.date).format("DD MMM YYYY")}
                                                                        </span>
                                                                        <span className="d-flex align-items-center gap-1">
                                                                            <MdAccessTime size={14} />
                                                                            {moment(dateTime.time, "HH:mm:ss").format("hh:mm A")}
                                                                        </span>
                                                                    </div>
                                                                )}

                                                            </div>
                                                            <div className="d-flex gap-2" onClick={(e) => e.stopPropagation()}>
                                                                {canEditPrescription() && (
                                                                    <Button
                                                                        variant="outline-primary"
                                                                        size="sm"
                                                                        onClick={() => handleEditPrescription(prescription)}
                                                                        className="d-flex align-items-center gap-1"
                                                                        style={{ background: '#3182ce', color: '#fff' }}
                                                                    >
                                                                        <FaEdit size={12} />
                                                                        <span className="d-none d-sm-inline">{t('edit')}</span>
                                                                    </Button>
                                                                )}
                                                                {canDelete() && (
                                                                    <Button
                                                                        variant="danger"
                                                                        size="sm"
                                                                        onClick={() => handleDeleteClick(prescription.id, `Prescription #${prescription.id}`)}
                                                                        className="d-flex align-items-center gap-1"
                                                                    >
                                                                        <FaTrash size={12} />
                                                                        <span className="d-none d-sm-inline">{t('delete')}</span>
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </Accordion.Header>
                                                    <Accordion.Body className="bg-white">
                                                        <Row className="g-4">
                                                            <Col md={12}>

                                                                <div className="p-3 bg-light rounded" style={{ overflowX: 'auto' }}>
                                                                    <Table striped bordered hover responsive>
                                                                        <thead>
                                                                            <tr>
                                                                                <th>{t('Product Name')}</th>
                                                                                <th>{t('Quantity')}</th>
                                                                                <th>{t('In Prescription')}</th>
                                                                                <th>{t('Actions')}</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {prescription.prescription_detail?.map((detail, idx) => {
                                                                                const isNewRow = detail.tempId;
                                                                                const selectedOption = detail.product_id ? productOptions.find(opt => opt.value === detail.product_id) : null;
                                                                                const stockWarning = detail.product_id && detail.quantity > (detail.stock || 0);
                                                                                const rowTotal = (detail.price || 0) * (detail.quantity || 1);

                                                                                return (
                                                                                    <tr key={detail.tempId || detail.id || idx}>
                                                                                        <td style={{ minWidth: '250px' }}>
                                                                                            {isNewRow ? (
                                                                                                <Select
                                                                                                    options={productOptions}
                                                                                                    value={selectedOption}
                                                                                                    onChange={(selected) => handleTableProductSelect(prescription.id, detail.tempId || detail.id, selected)}
                                                                                                    placeholder={t('Search and select medicine...')}
                                                                                                    isClearable
                                                                                                    menuPortalTarget={document.body}
                                                                                                    styles={{
                                                                                                        control: (base) => ({
                                                                                                            ...base,
                                                                                                            minHeight: '38px',
                                                                                                            fontSize: '14px'
                                                                                                        }),
                                                                                                        menuPortal: (base) => ({
                                                                                                            ...base,
                                                                                                            zIndex: 9999
                                                                                                        })
                                                                                                    }}
                                                                                                />
                                                                                            ) : (
                                                                                                <div>
                                                                                                    <strong>{detail.product?.product?.name || detail.product?.name || detail.product_name || 'N/A'}</strong>
                                                                                                    {detail.price > 0 && (
                                                                                                        <div className="text-muted small">
                                                                                                        </div>
                                                                                                    )}
                                                                                                </div>
                                                                                            )}
                                                                                        </td>
                                                                                        <td style={{ width: '100px' }}>
                                                                                            {isNewRow ? (
                                                                                                <Form.Control
                                                                                                    type="number"
                                                                                                    size="sm"
                                                                                                    min="1"
                                                                                                    max={detail.stock || 0}
                                                                                                    value={detail.quantity || 1}
                                                                                                    onChange={(e) => handleTableQuantityUpdate(prescription.id, detail.tempId || detail.id, parseInt(e.target.value) || 1)}
                                                                                                    disabled={!detail.product_id}
                                                                                                    style={{ textAlign: 'center' }}
                                                                                                    isInvalid={stockWarning}
                                                                                                />
                                                                                            ) : (
                                                                                                <span className="fw-bold">{detail.quantity}</span>
                                                                                            )}
                                                                                        </td>
                                                                                        <td style={{ width: '150px' }}>
                                                                                            {isNewRow ? (
                                                                                                <Form.Check
                                                                                                    type="checkbox"
                                                                                                    id={`in-prescription-${detail.tempId || detail.id}`}
                                                                                                    checked={detail.in_prescription === 1}
                                                                                                    onChange={() => handleTableInPrescriptionToggle(prescription.id, detail.tempId || detail.id)}
                                                                                                    disabled={!detail.product_id}
                                                                                                    label={
                                                                                                        <span className={detail.in_prescription === 1 ? 'text-success fw-bold ms-2' : 'text-muted ms-2'}>
                                                                                                            {detail.in_prescription === 1 ? t('Yes') : t('No')}
                                                                                                        </span>
                                                                                                    }
                                                                                                />
                                                                                            ) : (
                                                                                                <Badge bg={detail.in_prescription === 1 ? "success" : "secondary"}>
                                                                                                    {detail.in_prescription === 1 ? t('Yes') : t('No')}
                                                                                                </Badge>
                                                                                            )}
                                                                                        </td>
                                                                                        <td style={{ width: '80px' }}>
                                                                                            <Button
                                                                                                variant="danger"
                                                                                                size="sm"
                                                                                                onClick={() => handleTableRemoveRow(prescription.id, detail.tempId || detail.id)}
                                                                                            >
                                                                                                <FaTrashAlt size={12} />
                                                                                            </Button>
                                                                                        </td>
                                                                                    </tr>
                                                                                );
                                                                            })}
                                                                            {(!prescription.prescription_detail || prescription.prescription_detail.length === 0) && (
                                                                                <tr>
                                                                                    <td colSpan="6" className="text-center text-muted py-4">
                                                                                        <FaBoxes size={30} className="mb-2 d-block mx-auto" />
                                                                                        {t('No items in this prescription')}
                                                                                        <br />
                                                                                        <Button
                                                                                            variant="link"
                                                                                            size="sm"
                                                                                            onClick={() => handleAddTableRow(prescription.id)}
                                                                                            className="mt-2"
                                                                                        >
                                                                                            <FaPlus size={12} className="me-1" />
                                                                                            {t('Click here to add items')}
                                                                                        </Button>
                                                                                    </td>
                                                                                </tr>
                                                                            )}
                                                                        </tbody>
                                                                    </Table>

                                                                    {noteText && (
                                                                        <div className="mt-3 pt-2 border-top">
                                                                            <div className="d-flex align-items-center gap-2">
                                                                                <FaFileAlt className="text-success" />
                                                                                <strong className="text-muted">{t('note')}:</strong>
                                                                            </div>
                                                                            <p className="mb-0 mt-1 text-secondary">{noteText}</p>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    </Accordion.Body>
                                                </Accordion.Item>
                                            );
                                        })}
                                    </Accordion>
                                </Accordion.Body>
                            </Accordion.Item>
                        );
                    })}
                </Accordion>
            )}

            <Modal show={showModal} onHide={() => setShowModal(false)} size="xl" style={{ '--bs-modal-width': '1400px' }}>
                <Modal.Header closeButton className="custom-modal-header">
                    <Modal.Title className="custom-modal-title">
                        {editingPrescription ? t('Edit Prescription') : t('Add Prescription')}
                    </Modal.Title>
                </Modal.Header>

                <Form onSubmit={handleSubmit}>
                    <Modal.Body className="custom-modal-body" style={{ padding: '1.5rem' }}>
                        <div className="mb-4">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="fw-bold mb-0 d-flex align-items-center gap-2">
                                    <FaPills className="text-success" />
                                    {t('Prescription Items')}
                                </h6>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={handleAddNewRow}
                                    className="d-flex align-items-center gap-1"
                                >
                                    <FaPlus size={12} /> {t('Add Medicine')}
                                </Button>
                            </div>

                            {selectedProducts.length === 0 ? (
                                <div className="text-center text-muted py-4 border rounded">
                                    <FaShoppingCart size={30} className="mb-2 d-block mx-auto" />
                                    {t('No products selected. Click "Add Medicine" to add items.')}
                                </div>
                            ) : (
                                <div className="selected-products-list">
                                    {selectedProducts.map((product, index) => {
                                        const selectedOption = product.product_id ? productOptions.find(opt => opt.value === product.product_id) : null;
                                        const stockWarning = product.product_id && product.quantity > (product.stock || '');

                                        return (
                                            <div key={product.id} className="product-row mb-3 p-3 border rounded bg-light">
                                                <div className="row g-3 align-items-center">
                                                    <div className="col-md-5">
                                                        <label className="form-label small fw-bold mb-1">{t('Medicine')} <span className="text-danger">*</span></label>
                                                        <Select
                                                            options={productOptions}
                                                            value={selectedOption}
                                                            onChange={(selected) => handleProductSelect(selected?.value, product.id, selected)}
                                                            onInputChange={(inputValue) => setProductSearchTerms(prev => ({ ...prev, [product.id]: inputValue }))}
                                                            inputValue={productSearchTerms[product.id] || ''}
                                                            placeholder={t('Search and select medicine...')}
                                                            isClearable
                                                            menuPortalTarget={document.body}
                                                            styles={{
                                                                control: (base) => ({
                                                                    ...base,
                                                                    minHeight: '38px',
                                                                    fontSize: '14px'
                                                                }),
                                                                menuPortal: (base) => ({
                                                                    ...base,
                                                                    zIndex: 9999
                                                                })
                                                            }}
                                                        />
                                                        {stockWarning && (
                                                            <small className="text-danger d-block mt-1">
                                                                {t('Stock available')}: {product.stock}
                                                            </small>
                                                        )}
                                                    </div>

                                                    <div className="col-md-2">
                                                        <label className="form-label small fw-bold mb-1">{t('Quantity')}</label>
                                                        <Form.Control
                                                            type="number"
                                                            size="sm"
                                                            max={product.stock || ''}
                                                            value={product.quantity || ''}
                                                            onClick={(e) => e.target.select()}
                                                            onChange={(e) => handleUpdateQuantity(product.id, parseInt(e.target.value) || 1)}
                                                            disabled={!product.product_id}
                                                            style={{ textAlign: 'center' }}
                                                            isInvalid={stockWarning}
                                                        />
                                                    </div>

                                                    <div className="col-md-2">
                                                        <label className="form-label small fw-bold mb-1">{t('Price')}</label>
                                                        <Form.Control
                                                            type="number"
                                                            size="sm"
                                                            value={product.price || ''}
                                                            placeholder="Enter Qty"
                                                            onChange={(e) => handleUpdatePrice(product.id, e.target.value)}
                                                            disabled={!product.product_id}
                                                            style={{ textAlign: 'center' }}
                                                            className="border-primary"
                                                        />
                                                    </div>

                                                    <div className="col-md-2">
                                                        <label className="form-label small fw-bold mb-1">{t('In Prescription')}</label>
                                                        <div className="d-flex align-items-center">
                                                            <Form.Check
                                                                type="checkbox"
                                                                id={`in-prescription-${product.id}`}
                                                                checked={product.in_prescription === 1}
                                                                placeholder="Enter Price"
                                                                onChange={() => handleToggleInPrescription(product.id)}
                                                                disabled={!product.product_id}
                                                                label={
                                                                    <span className={product.in_prescription === 1 ? 'text-success fw-bold ms-2' : 'text-muted ms-2'}>
                                                                        {product.in_prescription === 1 ? t('Yes') : t('No')}
                                                                    </span>
                                                                }
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="col-md-1 text-end">
                                                        <Button
                                                            variant="danger"
                                                            size="sm"
                                                            onClick={() => handleRemoveProduct(product.id)}
                                                            style={{ backgroundColor: '#dc3545', borderColor: '#dc3545' }}
                                                        >
                                                            <FaTrashAlt size={12} />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        <div>
                            <Form.Label className="fw-bold d-flex align-items-center gap-2">
                                <FaFileAlt /> {t('Note')}
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={generalNote}
                                onChange={(e) => setGeneralNote(e.target.value)}
                                placeholder={t('e.g Don\'t give dairy products')}
                            />
                        </div>
                    </Modal.Body>

                    <Modal.Footer className="custom-modal-footer">
                        <Button
                            variant="secondary"
                            onClick={() => setShowModal(false)}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            disabled={submitting || selectedProducts.filter(p => p.product_id && p.quantity <= (p.stock || 0)).length === 0}
                        >
                            {submitting ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    {t('saving')}
                                </>
                            ) : (
                                editingPrescription ? t('update') : t('save')
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
}